import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import jwt_decode from 'jwt-decode';

import { AddEditFacilityItemModal } from './ActionModals/AddEditFacilityItemModal';
import { DeleteFacilityItemConfirmationModal } from './ActionModals/DeleteFacilityItemConfirmationModal';
import { FacilityItemsTable } from './Components/FacilityItemsTable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateReport } from './Components/GenerateReport';
import {
  getAllFacilityItems,
  createFacilityItem,
  updateFacilityItem,
  deleteFacilityItem,
  getPropertyList,
} from './Helpers/Api';
import {
  getValidationSchema,
  getInitialValues,
} from './Helpers/Config';
import { generateReportOptions } from './Helpers/Utils';

const FacilityInventoryManagement = () => {
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [facilityItems, setFacilityItems] = useState([]);
  const [modal, setModal] = useState('');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [user, setUser] = useState(null);

  const toggleReportModal = () => {
    if (!selectedProperty.property_id || !selectedProperty.building_name) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a property and building before generating a report.',
      });
      return;
    }
    setIsReportModalOpen(!isReportModalOpen);
  };

  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState({
    property_id: '',
    building_name: '',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getPropertyList(user?.role || '', user?.id || '');
      setProperties(result?.propertyList || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to fetch properties.',
      });
      setProperties([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, user]);

  const fetchFacilityItems = useCallback(async (propertyId, buildingName) => {
    if (!propertyId || !buildingName) {
      setFacilityItems([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { items } = await getAllFacilityItems(propertyId, buildingName);
      setFacilityItems(items);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch facility items.',
      });
      setFacilityItems([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const handlePropertySelectionChange = useCallback((newSelection) => {
    setSelectedProperty(newSelection);
  }, []);

  const formik = useFormik({
    initialValues: getInitialValues(modal, selectedItem, selectedProperty),
    validationSchema: getValidationSchema(modal),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (!selectedProperty.property_id || !selectedProperty.building_name) {
        Swal.fire({
          icon: 'warning',
          title: 'Selection Required',
          text: 'Please select a property and building before submitting.',
        });
        setSubmitting(false);
        return;
      }

      if (!user) {
        Swal.fire({
          icon: 'error',
          title: 'Authentication Error',
          text: 'User not authenticated. Please log in.',
        });
        setSubmitting(false);
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const facilityData = {
          ...values,
          property_id: selectedProperty.property_id,
          building_name: selectedProperty.building_name,
        };

        if (modal === 'add') {
          const facilityItemData = {
            ...facilityData,
            items: values.items.map(item => ({
              ...item,
              requestsSupported: item.requestsSupported || [], // Ensure array
              images: item.images?.filter(img => img.url).map(img => ({
                ...img,
                uploadedBy: {
                  id: user?.id || '',
                  role: user?.role || '',
                },
              })) || [],
              invoices: item.invoices?.filter(inv => inv.url).map(inv => ({
                ...inv,
                uploadedBy: {
                  id: user?.id || '',
                  role: user?.role || '',
                },
              })) || [],
            })),
          };

          await createFacilityItem(facilityItemData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Facility items saved successfully!',
          });
        } else if (modal === 'edit') {
          const editData = {
            item_id: values.item_id,
            property_id: selectedProperty.property_id,
            building_name: selectedProperty.building_name,
            item: {
              ...values.item,
              requestsSupported: values.item.requestsSupported || [], // Ensure array
              images: values.item.images?.filter(img => img.url).map(img => ({
                ...img,
                uploadedBy: {
                  id: user?.id || '',
                  role: user?.role || '',
                },
              })) || [],
              invoices: values.item.invoices?.filter(inv => inv.url).map(inv => ({
                ...inv,
                uploadedBy: {
                  id: user?.id || '',
                  role: user?.role || '',
                },
              })) || [],
            },
          };

          await updateFacilityItem(editData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Facility item updated successfully!',
          });
        }

        await fetchFacilityItems(selectedProperty.property_id, selectedProperty.building_name);
        closeModal();
        resetForm({ values: getInitialValues('', null, selectedProperty) });
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.message });
        resetForm({ values: getInitialValues('', null, selectedProperty) });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        setSubmitting(false);
      }
    },
  });

  const openAddModal = () => {
    if (!selectedProperty.property_id || !selectedProperty.building_name) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a property and building before adding an item.',
      });
      return;
    }
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(null);
    setModal('add');
  };

  const openEditModal = (item) => {
    if (!selectedProperty.property_id || !selectedProperty.building_name) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a property and building before editing an item.',
      });
      return;
    }
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(item);
    setModal('edit');
  };

  const openDeleteModal = (item) => {
    if (!selectedProperty.property_id || !selectedProperty.building_name) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a property and building before deleting an item.',
      });
      return;
    }
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(item);
    setModal('delete');
  };

  const closeModal = () => {
    formik.resetForm({
      values: getInitialValues('', null, selectedProperty),
    });
    setModal('');
    setSelectedItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteFacilityItem(selectedItem._id);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Facility item deleted successfully!',
      });
      await fetchFacilityItems(selectedProperty.property_id, selectedProperty.building_name);
      closeModal();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Initialize user on component mount
  useEffect(() => {
    const userData = getUserFromToken();
    setUser(userData);
  }, [getUserFromToken]);

  useEffect(() => {
    if (user) {
      fetchProperties();
    }
  }, [fetchProperties, user]);

  useEffect(() => {
    if (user) {
      fetchFacilityItems(selectedProperty.property_id, selectedProperty.building_name);
    }
  }, [selectedProperty.property_id, selectedProperty.building_name, fetchFacilityItems, user]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Facility Inventory Management</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Inventory" breadcrumbItem="Facility Items" />

        <PropertySelector
          user={user}
          propertyList={properties}
          selectedProperty={selectedProperty}
          onPropertySelectionChange={handlePropertySelectionChange}
          onAddClick={openAddModal}
          setFacilityItems={setFacilityItems}
          toggleReportModal={toggleReportModal}
        />

        <GenerateReport
          user={user}
          propertyId={selectedProperty.property_id}
          buildingName={selectedProperty.building_name}
          toggle={toggleReportModal}
          modal={isReportModalOpen}
          options={generateReportOptions(facilityItems)}
        />

        <FacilityItemsTable
          user={user}
          items={facilityItems}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

        <AddEditFacilityItemModal
          user={user}
          open={modal === 'add' || modal === 'edit'}
          mode={modal}
          formik={formik}
          onClose={closeModal}
          propertyList={properties}
          selectedProperty={selectedProperty}
          itemId={selectedItem?._id || ''}
        />

        <DeleteFacilityItemConfirmationModal
          user={user}
          open={modal === 'delete'}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          item={selectedItem}
        />
      </div>
    </div>
  );
};

export { FacilityInventoryManagement };